import React, { useEffect, useState } from 'react';
import { Laptop, Loader } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";

const getStatusBadge = (status) => {
  switch (status) {
    case 'online':
      return <Badge className="bg-gray-800 hover:bg-gray-900 text-white text-xs font-medium px-2.5 py-1 rounded-full">online</Badge>;

    case 'warning':
      return <Badge className="bg-yellow-400 hover:bg-yellow-500 text-white text-xs font-medium px-2.5 py-1 rounded-full">warning</Badge>;

    case 'error':
      return <Badge className="bg-red-500 hover:bg-red-600 text-white text-xs font-medium px-2.5 py-1 rounded-full">error</Badge>;

    case 'checking':
      return <Badge className="bg-blue-400 hover:bg-blue-500 text-white text-xs font-medium px-2.5 py-1 rounded-full animate-pulse">checking</Badge>

    default:
      return null;
  }
};

const getBorderColor = (status) => {
  switch (status) {
    case 'online':
      return 'border-l-green-500';

    case 'warning':
      return 'border-l-yellow-500';

    case 'error':
      return 'border-l-red-500';
    
    case 'checking':
      return 'border-l-blue-500';

    default:
      return null;
  }
}

const getIssueBar = (issues) => {
  const maxIssues = 20;
  const value = (issues / maxIssues) * 100;
  
  return (
    <div className="flex items-center gap-2">
      <Progress value={value} className="h-2 w-24" />
      <span className="font-medium">{issues}</span>
    </div>
  );
};

const ApplicationStatus = ({ applications, isLoading }) => {
  const [appsWithStatus, setAppsWithStatus] = useState([]);

  useEffect(() => {
    if (!applications || !Array.isArray(applications)) {
      return;
    }

    applications.forEach((app) => app.status = 'checking');
    setAppsWithStatus(applications);
    
    const fetchSingleStatus = async (url) => {
      try {
        await fetch(url, { mode: 'no-cors' });

        return 'online';
      } catch {
        return 'error';
      }
    };
    

    const fetchStatusesInParallel = async () => {
      const updatedApps = [...applications];

      await Promise.all(
        updatedApps.map(async (app, index) => {
          const status = await fetchSingleStatus(app.url);
          updatedApps[index].status = status;

          setAppsWithStatus([...updatedApps]);
        })
      );
    };
  
    fetchStatusesInParallel();
  }, [applications]);
  
  return (
    <div>
      <div className="flex flex-col mb-4">
        <div className="flex items-center gap-2">
          { isLoading ? <Loader className="h-5 w-5 text-indigo-500 animate-spin" /> : <Laptop className="h-5 w-5 text-indigo-500" /> }
          <h2 className="text-lg font-semibold">Application Status</h2>
        </div>
        <p className="text-sm text-gray-500 ml-7">Critical education app performance</p>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="text-left text-sm text-gray-500">
              <th className="pb-3 font-medium w-1/3">Application</th>
              <th className="pb-3 font-medium w-1/5">Status</th>
              <th className="pb-3 font-medium w-1/5">Uptime</th>
              <th className="pb-3 font-medium w-1/4">Issues</th>
            </tr>
          </thead>
          <tbody>
            {appsWithStatus?.map((app, index) => (
              <tr 
                key={index} 
                className={`border-t border-solid border-slate-200 border-l-4 ${getBorderColor(app.status)}`}
              >
                <td className="py-4 font-medium pl-2">
                  {app.name}
                </td>
                <td className="py-4">
                  {getStatusBadge(app.status)}
                </td>
                <td className="py-4 text-sm font-medium">
                  {app.uptime}
                </td>
                <td className="py-4">
                  {getIssueBar(app.issuesNumber)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* "View All Applications" Button */}
      <div className="flex justify-end mt-5">
        <Link to="/admin/applications-settings" className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors">
          View All Applications →
        </Link>
      </div>
    </div>
  );
};

export default ApplicationStatus;
